import { ActiveDiscount } from '@/models/ActiveDiscount'
import { PriceInStorage, ProductVariant } from '@/models/Product'
import { getLocalStorageKey, isArray, setLocalStorageKey } from '@/utils/helper'

export interface VariantPricing {
  mrp: number
  discount: number
  salePrice: number
  isFlashSale: boolean
  activeDiscount: ActiveDiscount | null
  discountPercent: number
}

const EMPTY_VARIANT_PRICING: VariantPricing = {
  mrp: 0,
  discount: 0,
  salePrice: 0,
  isFlashSale: false,
  activeDiscount: null,
  discountPercent: 0,
}

export function isActiveFlashSaleDiscount(
  activeDiscount: ActiveDiscount | null | undefined,
): activeDiscount is ActiveDiscount {
  if (activeDiscount == null) {
    return false
  }

  const remainingSeconds = Number(activeDiscount.time_remaining_seconds)
  if (Number.isFinite(remainingSeconds) && remainingSeconds > 0) {
    return true
  }

  if (activeDiscount.ends_at) {
    const endsAtMs = new Date(activeDiscount.ends_at).getTime()
    if (!Number.isNaN(endsAtMs) && endsAtMs > Date.now()) {
      return true
    }
  }

  return false
}

export function getVariantPricing(
  variant?: ProductVariant | null,
): VariantPricing {
  if (!variant) {
    return EMPTY_VARIANT_PRICING
  }

  const inventory = isArray(variant.inventory_set)
    ? variant.inventory_set[0]
    : null
  const mrp = Number(inventory?.price) || 0
  const catalogDiscount = Number(inventory?.discount) || 0
  const activeDiscount = variant.active_discount ?? null

  if (isActiveFlashSaleDiscount(activeDiscount)) {
    const discount = Number(activeDiscount.discount_amount) || 0
    const salePrice = Math.max(mrp - discount, 0)
    const discountPercent = mrp > 0 ? Math.round((discount / mrp) * 100) : 0

    return {
      mrp,
      discount,
      salePrice,
      isFlashSale: true,
      activeDiscount,
      discountPercent,
    }
  }

  const salePrice = Math.max(mrp - catalogDiscount, 0)
  const discountPercent =
    mrp > 0 ? Math.round((catalogDiscount / mrp) * 100) : 0

  return {
    mrp,
    discount: catalogDiscount,
    salePrice,
    isFlashSale: false,
    activeDiscount: null,
    discountPercent,
  }
}

export type FlashSaleTimeFormat =
  | { unit: 'days'; days: number }
  | { unit: 'minutes'; minutes: number }
  | { unit: 'hours'; hours: string; minutes: string }

const DAY_SECONDS = 24 * 3600
const HOUR_SECONDS = 3600

/** Formats remaining sale time: days if > 24h, minutes if < 1h, else HH:MMHr. */
export function formatFlashSaleTime(totalSeconds: number): FlashSaleTimeFormat {
  const safeSeconds = Math.max(totalSeconds, 0)

  if (safeSeconds > DAY_SECONDS) {
    return { unit: 'days', days: Math.floor(safeSeconds / DAY_SECONDS) }
  }

  if (safeSeconds < HOUR_SECONDS) {
    const minutes = Math.max(1, Math.ceil(safeSeconds / 60))
    return { unit: 'minutes', minutes }
  }

  const hours = Math.floor(safeSeconds / HOUR_SECONDS)
  const minutes = Math.floor((safeSeconds % HOUR_SECONDS) / 60)
  return {
    unit: 'hours',
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
  }
}

export function buildProductOrderDetailLine({
  variant,
  quantity,
  catalogueId,
  groupId,
}: {
  variant: ProductVariant
  quantity: number
  catalogueId?: number
  groupId?: Record<string, unknown>
}) {
  const pricing = getVariantPricing(variant)
  const line: Record<string, unknown> = {
    product_variant: Number(variant.id),
    quantity,
    price: pricing.mrp * quantity,
    discount: pricing.discount * quantity,
    c_id: catalogueId,
    ...groupId,
  }

  if (pricing.isFlashSale) {
    line.is_time_based_discount = true
  }

  return line
}

export function getCartUpdatePricing(variant: ProductVariant) {
  const pricing = getVariantPricing(variant)
  return {
    variantPrice: pricing.mrp,
    variantDiscount: pricing.discount,
    activeDiscount: pricing.activeDiscount,
  }
}

function buildPricingSnapshot(variant: ProductVariant) {
  const pricing = getVariantPricing(variant)
  return {
    price: pricing.mrp,
    discount: pricing.discount,
    active_discount: variant.active_discount ?? null,
  }
}

function isPricingSnapshotEqual(
  a: PriceInStorage[string] | undefined,
  b: PriceInStorage[string],
) {
  if (!a) {
    return false
  }

  return (
    a.price === b.price &&
    a.discount === b.discount &&
    a.active_discount?.discount_amount === b.active_discount?.discount_amount &&
    a.active_discount?.time_remaining_seconds ===
      b.active_discount?.time_remaining_seconds
  )
}

/** Refreshes pp_d from catalog API variants so stale local snapshots cannot override live pricing. */
export function syncCartPricingFromCatalog(
  variants: ProductVariant[],
): ProductVariant[] {
  if (typeof window === 'undefined') {
    return variants
  }

  const storedPricing =
    getLocalStorageKey<PriceInStorage, PriceInStorage>('pp_d', {}) || {}
  let hasChanges = false

  variants.forEach((variant) => {
    const nextSnapshot = buildPricingSnapshot(variant)
    if (!isPricingSnapshotEqual(storedPricing[variant.id], nextSnapshot)) {
      storedPricing[variant.id] = nextSnapshot
      hasChanges = true
    }
  })

  if (hasChanges) {
    setLocalStorageKey('pp_d', storedPricing)
  }

  return variants
}

